import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { IResponseAPI } from '@shared/interfaces/response-api.interface';
import { INotificarEmpleadoresBody } from '../data/models/sanna.interfaces';

import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import {
  IConsultarCiudadanoAlerta,
  IEspacialidades,
  IModificarCiudadanoResponse,
  IModificarProfesionalResponse,
  IModificarProfesionalParams,
  IObtenerCiudadanoResponse,
  IPrestador,
  IModificarCiudadanoParams,
} from '../data/models/sanna-ciudadano-api.interface';
import { ConstantsService } from './constants.service';
import { GeneralService } from '@shared/services/general.service';
import {
  abstractResolutionConsultationResponse,
  abstractResolutionConsultationResponseItem,
} from '../interface/abstract.interface';

@Injectable({
  providedIn: 'root',
})
export class SannaCiudadanoApiService {
  private urls = {
    obtenerCiudadano: environment.apiBaseUrl + '/ciudadanos/v1/ciudadano/',
    modificarCiudadano: environment.apiBaseUrl + '/ciudadanos/v1/ciudadanos/',
    modificarProfesional: environment.apiBaseUrl + '/ciudadanos/v1/ciudadanos/profesional/',
    consultarPrestador: environment.apiBaseUrl + '/ciudadanos/v1/consultar/prestador/',
    obtenerEspecialidades: environment.apiBaseUrl + '/mantenedores-se/v1/especialidades',
    consultarCiudadanoAlerta: environment.apiBaseUrl + '/sanna-ciudadanos/v1/consultar-ciudadano-alerta/',
    notificacionCorreoSolicitudAntecedentes: environment.apiBaseUrl + '/notificaciones/v1/correo-sanna-solicitud',
    obtenerConsultaResolucionDetalle: environment.apiBaseUrl + '/sanna-ext/v1/entidad/licencias/',
  };

  constructor(
    private httpClient: HttpClient,
    private generalService: GeneralService,
    private constantsService: ConstantsService,
  ) {}

  /**
   * Obtiene datos del ciudadano
   *
   * @param rutSdv10Digitos - Rut sin digito verificador (ni puntos). Ej: 12345678-9
   * @returns Objeto (IObtenerCiudadanoResponse) con datos del ciudadano
   */
  obtenerCiudadano(rutSdv10Digitos: string): Observable<IObtenerCiudadanoResponse> {
    return this.httpClient.get<IObtenerCiudadanoResponse>(this.urls.obtenerCiudadano + rutSdv10Digitos).pipe(
      tap((_) => this.generalService.log('obtenerCiudadano fetched!')),
      catchError(
        this.generalService.handleError<IObtenerCiudadanoResponse>('obtenerCiudadano', {
          estado: false,
          metadatos: {
            codigo_respuesta: 999,
            accion: this.constantsService.mensaje.error.inesperado,
          },
        }),
      ),
    );
  }

  /**
   * Obtiene Espacialidades
   *
   * @returns Listado de Espacialidades
   */
  obtenerEspecialidades(): Observable<IEspacialidades> {
    return this.httpClient.get<IEspacialidades>(this.urls.obtenerEspecialidades).pipe(
      tap((_) => this.generalService.log('obtenerEspecialidades fetched!')),
      catchError(
        this.generalService.handleError<IEspacialidades>('obtenerEspecialidades', {
          estado: false,
          metadatos: {
            codigo_respuesta: 999,
            accion: this.constantsService.mensaje.error.inesperado,
          },
        }),
      ),
    );
  }

  /**
   * Actualiza datos del ciudadano
   *
   * @param params - Objeto (IModificarCiudadanoParams) con datos a actualizar
   * @param rutSinDv - Rut sin digito verificador (ni puntos). Ej: 12345678-9
   * @returns Objeto (IModificarCiudadanoResponse) con datos de ciudadano actualizado
   */
  modificarCiudadano(params: IModificarCiudadanoParams, rutSinDv: string): Observable<IModificarCiudadanoResponse> {
    return this.httpClient.put<IModificarCiudadanoResponse>(this.urls.modificarCiudadano + rutSinDv, params);
  }

  /**
   * Actualiza datos de ciudadano Profesional
   *
   * @param params - Objeto (IModificarProfesionalParams) con datos a actualizar
   * @param rutSinDv - Rut sin digito verificador (ni puntos). Ej: 12345678-9
   * @returns Objeto (IModificarProfesionalResponse) con datos de ciudadano Profesional actualizado
   */
  modificarProfesional(
    params: IModificarProfesionalParams,
    rutSinDv: string,
  ): Observable<IModificarProfesionalResponse> {
    return this.httpClient.put<IModificarProfesionalResponse>(this.urls.modificarProfesional + rutSinDv, params);
  }

  /**
   * Notifica por correo electronico al Trabajador
   */
  notificacionCorreoSolicitudAntecedentes(params: INotificarEmpleadoresBody): Observable<IResponseAPI> {
    return this.httpClient.post<IResponseAPI>(this.urls.notificacionCorreoSolicitudAntecedentes, params);
  }

  consultarPrestador(fullRut: string): Observable<IPrestador> {
    return this.httpClient.get<IPrestador>(this.urls.consultarPrestador + fullRut).pipe(
      tap((_) => this.generalService.log('consultarPrestador fetched!')),
      catchError(
        this.generalService.handleError<IPrestador>('consultarPrestador', {
          estado: false,
          metadatos: {
            codigo_respuesta: 999,
            accion: this.constantsService.mensaje.error.inesperado,
          },
        }),
      ),
    );
  }

  consultarCiudadanoAlerta(rut: string): Observable<IConsultarCiudadanoAlerta> {
    return this.httpClient.get<IConsultarCiudadanoAlerta>(this.urls.consultarCiudadanoAlerta + rut).pipe(
      tap((_) => this.generalService.log('consultarCiudadanoAlerta fetched!')),
      catchError(
        this.generalService.handleError<IConsultarCiudadanoAlerta>('consultarCiudadanoAlerta', {
          estado: false,
        }),
      ),
    );
  }

  /**
   * Obtiene Detalle de LM para Consulta de Resoluciones
   */
  obtenerConsultaResolucionDetalle(folio: string): Observable<abstractResolutionConsultationResponseItem[]> {
    return this.httpClient
      .get<abstractResolutionConsultationResponse>(this.urls.obtenerConsultaResolucionDetalle + folio)
      .pipe(map((response) => response.items || null));
  }
}
